import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Chip,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
  Button,
  AppBar,
  Toolbar,
} from '@mui/material';
import {
  ArrowBack,
  Notifications,
  Event,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContextNew';
import BottomNav from '../components/BottomNavigation';
import BrandLoader from '../components/BrandLoader';
import notificationService from '../services/notificationService';
import useResponsive from '../hooks/useResponsive';

const NotificationsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { 
    isMobile, 
    isTablet, 
    isDesktop,
    getResponsiveValue,
    getResponsiveSpacing,
    getDialogProps 
  } = useResponsive();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(0); // Will be set based on unread notifications
  
  
  // Ref to track if we've already marked notifications as read
  const hasMarkedAsRead = useRef(false);

  const tabs = ['General', 'Bookings', 'Reviews'];

  // Set flag that user has viewed notifications
  useEffect(() => {
    localStorage.setItem('notificationsViewed', 'true');
  }, []);

  // Mark all notifications as read when user visits the page
  const markAllAsRead = async () => {
    try {
      const unreadNotifications = notifications.filter(n => !n.isRead);
      if (unreadNotifications.length === 0) return;

      // Mark each unread notification as read
      const promises = unreadNotifications.map(notification => 
        notificationService.markAsRead(notification._id || notification.id)
      );
      
      await Promise.all(promises);
      
      // Update local state
      setNotifications(prev => 
        prev.map(n => ({ ...n, isRead: true }))
      );
      
    //  console.log(`✅ Marked ${unreadNotifications.length} notifications as read`);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Fetch notifications from backend
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const backendNotifications = await notificationService.fetchNotifications();
      setNotifications(backendNotifications);
    //  console.log('Fetched notifications:', backendNotifications.length);
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  // Set initial active tab based on unread notifications
  useEffect(() => {
    if (notifications.length > 0) {
      // Calculate unread counts for each tab (inline logic to avoid dependency issues)
      const tabCounts = tabs.map((_, index) => {
        let tabNotifications = [];
        
        switch (index) {
          case 0: // General
            tabNotifications = notifications.filter(n => {
              const type = n.type || '';
              const title = (n.title || '').toLowerCase();
              const body = (n.body || n.message || '').toLowerCase();
              
              return type === 'general' || 
                     type === 'system' || 
                     type === 'marketing' || 
                     type === 'push' || 
                     type === 'email' || 
                     type === 'sms' ||
                     type === 'gems_addition' ||
                     type === 'chat_with_provider' ||
                     type === 'gemma_chatbot' ||
                     type === 'order_status_change' ||
                     type === 'order' ||
                     type === 'payment' ||
                     type === 'offer' ||
                     !type ||
                     title.includes('general') || title.includes('system') || title.includes('marketing') ||
                     title.includes('order') || title.includes('product') || title.includes('chat') ||
                     title.includes('gem') || title.includes('reward') || title.includes('message') ||
                     title.includes('payment') || title.includes('offer') || title.includes('promotion') ||
                     body.includes('general') || body.includes('system') || body.includes('marketing') ||
                     body.includes('order') || body.includes('product') || body.includes('chat') ||
                     body.includes('gem') || body.includes('reward') || body.includes('message') ||
                     body.includes('payment') || body.includes('offer') || body.includes('promotion');
            });
            break;
            
          case 1: // Bookings
            tabNotifications = notifications.filter(n => {
              const type = n.type || '';
              const title = (n.title || '').toLowerCase();
              const body = (n.body || n.message || '').toLowerCase();
              
              return type.includes('booking') || 
                     type === 'booking_notification' ||
                     type === 'booking_reminder_24h' ||
                     type === 'booking_reminder_6h' ||
                     type === 'booking_reminder_1h' ||
                     type === 'booking_status_change' ||
                     type === 'booking_completion_review' ||
                     title.includes('booking') ||
                     body.includes('booking') ||
                     title.includes('appointment') ||
                     body.includes('appointment');
            });
            break;
            
          case 2: // Reviews
            tabNotifications = notifications.filter(n => {
              const type = n.type || '';
              const title = (n.title || '').toLowerCase();
              const body = (n.body || n.message || '').toLowerCase();
              
              return type === 'booking_completion_review' ||
                     type === 'new_review' ||
                     title.includes('review') ||
                     body.includes('review') ||
                     title.includes('rating') ||
                     body.includes('rating') ||
                     title.includes('feedback') ||
                     body.includes('feedback');
            });
            break;
            
          default:
            tabNotifications = notifications;
        }
        
        return tabNotifications.filter(n => !n.isRead).length;
      });
      
      // Find the first tab with unread notifications
      let initialTab = 0; // Default to first tab
      for (let i = 0; i < tabCounts.length; i++) {
        if (tabCounts[i] > 0) {
          initialTab = i;
          break;
        }
      }
      
      setActiveTab(initialTab);
   //   console.log('Set initial active tab to:', initialTab, 'with unread count:', tabCounts[initialTab]);
    }
  }, [notifications]);

  // Mark all notifications as read after they are fetched (only once)
  useEffect(() => {
    if (notifications.length > 0 && !hasMarkedAsRead.current) {
      hasMarkedAsRead.current = true;
      markAllAsRead();
    }
  }, [notifications]);

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      const success = await notificationService.markAsRead(notificationId);
      if (success) {
        // Update local state
        setNotifications(prev => 
          prev.map(n => 
            n._id === notificationId ? { ...n, isRead: true } : n
          )
        );
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  useEffect(() => {
    if (user) {
      fetchNotifications();
    } else {
      setLoading(false);
    }
  }, [user]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };



  // Helper function to get notifications for a specific tab
  const getNotificationsForTab = (tabIndex) => {
    switch (tabIndex) {
      case 0: // General
        return notifications.filter(n => {
          const type = n.type || '';
          const title = (n.title || '').toLowerCase();
          const body = (n.body || n.message || '').toLowerCase();
          
          // General notifications: system, marketing, general, push, email, sms, gems, chat, orders, etc.
          return type === 'general' || 
                 type === 'system' || 
                 type === 'marketing' || 
                 type === 'push' || 
                 type === 'email' || 
                 type === 'sms' ||
                 type === 'gems_addition' ||
                 type === 'chat_with_provider' ||
                 type === 'gemma_chatbot' ||
                 type === 'order_status_change' ||
                 type === 'order' ||  // From CustomerNotification model
                 type === 'payment' || // Payment notifications
                 type === 'offer' ||   // Special offers
                 !type ||
                 // Keywords for general notifications
                 title.includes('general') || title.includes('system') || title.includes('marketing') ||
                 title.includes('order') || title.includes('product') || title.includes('chat') ||
                 title.includes('gem') || title.includes('reward') || title.includes('message') ||
                 title.includes('payment') || title.includes('offer') || title.includes('promotion') ||
                 body.includes('general') || body.includes('system') || body.includes('marketing') ||
                 body.includes('order') || body.includes('product') || body.includes('chat') ||
                 body.includes('gem') || body.includes('reward') || body.includes('message') ||
                 body.includes('payment') || body.includes('offer') || body.includes('promotion');
        });
        
      case 1: // Bookings
        return notifications.filter(n => {
          const type = n.type || '';
          const title = (n.title || '').toLowerCase();
          const body = (n.body || n.message || '').toLowerCase();
          
          // Booking-related notifications
          return type.includes('booking') || 
                 type === 'booking_notification' ||
                 type === 'booking_reminder_24h' ||
                 type === 'booking_reminder_6h' ||
                 type === 'booking_reminder_1h' ||
                 type === 'booking_status_change' ||
                 type === 'booking_completion_review' ||
                 title.includes('booking') ||
                 body.includes('booking') ||
                 title.includes('appointment') ||
                 body.includes('appointment');
        });
        
      case 2: // Reviews
        return notifications.filter(n => {
          const type = n.type || '';
          const title = (n.title || '').toLowerCase();
          const body = (n.body || n.message || '').toLowerCase();
          
          // Review-related notifications
          return type === 'booking_completion_review' ||
                 type === 'new_review' ||
                 title.includes('review') ||
                 body.includes('review') ||
                 title.includes('rating') ||
                 body.includes('rating') ||
                 title.includes('feedback') ||
                 body.includes('feedback');
        });
        
      default:
        return notifications;
    }
  };

  const getFilteredNotifications = () => {
    return getNotificationsForTab(activeTab);
  };

  // Get notification counts for each tab (unread only)
  const getTabCounts = () => {
    return tabs.map((_, index) => {
      const tabNotifications = getNotificationsForTab(index);
      return tabNotifications.filter(n => !n.isRead).length;
    });
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) {
      return `${days} day${days > 1 ? 's' : ''} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      return 'Just now';
    }
  };

  const getNotificationIcon = (icon) => {
    switch (icon) {
      case 'booking':
        return <Event />;
      case 'reminder':
        return <Notifications />;
      case 'offer':
        return <Notifications />;
      case 'welcome':
        return <Notifications />;
      default:
        return <Notifications />;
    }
  };

  // Handle notification click for navigation
  const handleNotificationClick = async (notification) => {
    // Mark as read
    await markAsRead(notification._id || notification.id);
    
    // Handle navigation based on redirectScreen and redirectData
    if (notification.redirectScreen && notification.redirectData) {
      const { redirectScreen, redirectData } = notification;
      
      // Map redirectScreen to actual routes
      switch (redirectScreen) {
        case 'booking_details':
          if (redirectData.bookingId) {
            navigate(`/bookings/${redirectData.bookingId}`);
          } else {
            navigate('/bookings');
          }
          break;
          
        case 'bookings_list':
          navigate('/bookings');
          break;
          
        case 'order_details':
          if (redirectData.orderId) {
            navigate(`/orders/${redirectData.orderId}`);
          } else {
            navigate('/orders');
          }
          break;
          
        case 'orders_list':
          navigate('/orders');
          break;
          
        case 'wallet':
          navigate('/wallet');
          break;
          
        case 'profile':
          navigate('/profile');
          break;
          
        default:
          break;
      }
    }
  };

  const renderNotificationItem = (notification) => (
    <Card
      key={notification._id || notification.id}
      onClick={() => handleNotificationClick(notification)}
      sx={{
        mb: 2,
        borderRadius: 2,
        border: '1px solid #E5E7EB',
        boxShadow: 'none',
        bgcolor: notification.isRead ? 'transparent' : 'action.hover',
        borderLeft: notification.isRead ? 'none' : '4px solid',
        borderLeftColor: 'primary.main',
        cursor: 'pointer',
        '&:hover': {
          boxShadow: 2,
        }
      }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: '#006AF9',
              width: 40,
              height: 40
            }}
          >
            {getNotificationIcon(notification.icon || notification.type)}
          </Avatar>
          <Box sx={{ flexGrow: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: notification.isRead ? 400 : 600,
                color: '#15161E',
                mb: 0.5
              }}
            >
              {notification.title}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#15161E',
                mb: 1,
                lineHeight: 1.4
              }}
            >
              {notification.body || notification.message}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: '#606A85',
                fontWeight: 500
              }}
            >
              {formatTimestamp(new Date(notification.timestamp || notification.createdAt))}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );


  const renderEmptyState = () => {
    const getEmptyStateMessage = () => {
      switch (activeTab) {
        case 0: // General
          return {
            title: 'No general notifications',
            description: 'You\'ll see system updates, marketing messages, product orders, chat messages, and other general notifications here'
          };
        case 1: // Bookings
          return {
            title: 'No booking notifications',
            description: 'Booking confirmations, reminders, and status updates will appear here'
          };
        case 2: // Reviews
          return {
            title: 'No review notifications',
            description: 'Review requests and feedback notifications will appear here'
          };
        default:
          return {
            title: 'No notifications',
            description: 'You\'re all caught up!'
          };
      }
    };

    const message = getEmptyStateMessage();

    return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        textAlign: 'center'
      }}
    >
      <Notifications sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
      <Typography variant="h6" sx={{ color: 'grey.600', mb: 1 }}>
          {message.title}
      </Typography>
        <Typography variant="body2" sx={{ color: 'grey.500', maxWidth: 300 }}>
          {message.description}
      </Typography>
    </Box>
  );
  };

  return (
    <Box sx={{ pb: 8, backgroundColor: '#F1F4F8', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ backgroundColor: '#0A2A66' }}>
        <Toolbar>
          <IconButton 
            color="inherit" 
            onClick={() => navigate('/home')}
            sx={{ mr: 2 }}
          >
            <ArrowBack />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Notifications
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Tabs */}
      <Box sx={{ 
        backgroundColor: 'white', 
        borderBottom: 1, 
        borderColor: 'divider',
        position: 'sticky',
        top: 0,
        zIndex: 100
      }}>
        <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5'
        }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: 16,
                fontWeight: 500,
                color: 'grey.600',
                minHeight: 60,
                '&.Mui-selected': {
                  color: '#006AF9',
                  fontWeight: 600
                }
              },
              '& .MuiTabs-indicator': {
                backgroundColor: '#006AF9',
                height: 2
              }
            }}
          >
            {tabs.map((tab, index) => {
              const count = getTabCounts()[index];
              return (
                <Tab 
                  key={index} 
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{tab}</span>
                      {count > 0 && (
                        <Chip 
                          label={count} 
                          size="small" 
                          sx={{ 
                            height: 20, 
                            fontSize: '0.75rem',
                            backgroundColor: activeTab === index ? '#006AF9' : 'grey.300',
                            color: activeTab === index ? 'white' : 'grey.600'
                          }} 
                        />
                      )}
                    </Box>
                  } 
                />
              );
            })}
          </Tabs>
        </Box>
      </Box>


      {/* Content */}
      <Box sx={{ 
          flexGrow: 1,
          p: { xs: 2, md: 3 },
          width: '100%',
          backgroundColor: '#f5f5f5',
          mt: 2 
        }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <BrandLoader />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        ) : (
          <Box sx={{ py: 2 }}>
            {activeTab === 2 ? (
              // Reviews tab - show review notifications or empty state
              getFilteredNotifications().length > 0 ? (
                getFilteredNotifications().map(renderNotificationItem)
              ) : (
                renderEmptyState()
              )
            ) : getFilteredNotifications().length > 0 ? (
              // Show filtered notifications
              getFilteredNotifications().map(renderNotificationItem)
            ) : (
              // Empty state
              renderEmptyState()
            )}
          </Box>
        )}
      </Box>

      {/* Bottom Navigation */}
      <BottomNav />

    </Box>
  );
};

export default NotificationsPage;
